import { useState, useEffect } from "react";
import {
  getAboutPageForAdmin,
  updateAboutPage,
} from "../../../services/aboutPageService";
// @ts-ignore
import uploadService from "../../../services/uploadService";
import RichTextEditor from "../../../components/RichTextEditor";

interface AboutPageData {
  id?: string;
  story?: {
    image: string;
    title: string;
    paragraphs: string[];
    quote: string;
  };
  mission?: { title: string; content: string };
  vision?: { title: string; content: string };
  values?: {
    heading: string;
    subheading: string;
    items: Array<{
      icon: string;
      title: string;
      description: string;
      gradient: string;
    }>;
  };
  brandIdentity?: {
    badge: string;
    slogan: string;
    logoMeaning: string[];
    image: string;
  };
  services?: {
    heading: string;
    subheading: string;
    items: Array<{
      image: string;
      icon: string;
      title: string;
      description: string;
    }>;
  };
}

const GRADIENT_OPTIONS = [
  { value: "from-[#00c897] to-[#00b587]", label: "Xanh lá" },
  { value: "from-[#2a4b7c] to-[#1e3a5f]", label: "Xanh navy" },
];

const ImageField = ({
  label,
  value,
  onChange,
  objectId,
  objectType,
  objectField,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  objectId?: string;
  objectType?: string;
  objectField?: string;
  disabled?: boolean;
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      const r = new FileReader();
      r.onload = () => setPreview(r.result as string);
      r.readAsDataURL(f);
    }
  };

  const handleUpload = async () => {
    if (!file || !objectId || !objectType) return null;
    setUploading(true);
    try {
      const res = await uploadService.uploadFile(file, objectId, objectType, objectField);
      const url = res.data?.url || res.data?.publicUrl || res.url || "";
      if (url) onChange(url);
      setFile(null);
      setPreview("");
      return url;
    } catch (err: unknown) {
      console.error(err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const displayUrl = preview || value;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="space-y-2">
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          disabled={disabled}
        />
        {(objectId && objectType) && (
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="text-sm"
            />
            {file && (
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading}
                className="px-3 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 disabled:opacity-50"
              >
                {uploading ? "Đang upload..." : "Upload"}
              </button>
            )}
          </div>
        )}
        {displayUrl && (
          <div className="mt-2 relative inline-block">
            <img
              src={displayUrl}
              alt="Preview"
              className="max-h-32 rounded border"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <button
              type="button"
              onClick={() => {
                onChange("");
                setFile(null);
                setPreview("");
              }}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const TAB_MAP: Record<string, string> = {
  "about-story": "story",
  "about-mission": "mission",
  "about-values": "values",
  "about-brand": "brand",
  "about-services": "services",
};

interface AboutPageManagementProps {
  activeTabFromUrl?: string;
}

const AboutPageManagement = ({ activeTabFromUrl = "about-story" }: AboutPageManagementProps) => {
  const [data, setData] = useState<AboutPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: string; text: string }>({
    type: "",
    text: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAboutPageForAdmin();
      if (res.success && res.data) setData(res.data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || "Không thể tải dữ liệu");
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (payload: Partial<AboutPageData>) => {
    try {
      setSaving(true);
      setMessage({ type: "", text: "" });
      const res = await updateAboutPage(payload);
      if (res.success) {
        if (res.data) setData((prev) => ({ ...prev, ...res.data }));
        setMessage({
          type: "success",
          text: res.message || "Cập nhật thành công",
        });
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setMessage({
        type: "error",
        text: e.response?.data?.message || "Có lỗi khi lưu",
      });
    } finally {
      setSaving(false);
    }
  };

  const aboutPageId = data?.id || "";
  const activeSection = TAB_MAP[activeTabFromUrl] || "story";

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2A4B7C]"></div>
        <p className="mt-4 text-gray-600">Đang tải...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 text-sm text-red-600 hover:text-red-800 underline"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Quản lý trang Về chúng tôi
          </h2>
          <p className="text-gray-600 mt-1">
            Chỉnh sửa nội dung và ảnh trên trang giới thiệu
          </p>
        </div>
        <a
          href="/ve-chung-toi"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          <i className="ri-external-link-line mr-1"></i>
          Xem trang
        </a>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Story */}
        {activeSection === "story" && data && (
              <StorySection
                data={data.story}
                onSave={(d) => saveSection({ story: d })}
                saving={saving}
                message={message}
                objectId={aboutPageId}
              />
        )}
        {/* Mission & Vision */}
        {activeSection === "mission" && data && (
              <MissionVisionSection
                mission={data.mission}
                vision={data.vision}
                onSave={(d) => saveSection(d)}
                saving={saving}
                message={message}
              />
        )}
        {/* Values */}
        {activeSection === "values" && data && (
              <ValuesSection
                data={data.values}
                onSave={(d) => saveSection({ values: d })}
                saving={saving}
                message={message}
              />
        )}
        {/* Brand Identity */}
        {activeSection === "brand" && data && (
              <BrandIdentitySection
                data={data.brandIdentity}
                onSave={(d) => saveSection({ brandIdentity: d })}
                saving={saving}
                message={message}
                objectId={aboutPageId}
              />
        )}
        {/* Services */}
        {activeSection === "services" && data && (
              <ServicesSection
                data={data.services}
                onSave={(d) => saveSection({ services: d })}
                saving={saving}
                message={message}
                objectId={aboutPageId}
              />
        )}
      </div>
    </div>
  );
};

// --- Story Section ---
const StorySection = ({
  data,
  onSave,
  saving,
  message,
  objectId,
}: {
  data?: AboutPageData["story"];
  onSave: (d: NonNullable<AboutPageData["story"]>) => void;
  saving: boolean;
  message: { type: string; text: string };
  objectId: string;
}) => {
  const [form, setForm] = useState({
    image: "",
    title: "Câu Chuyện Stackway",
    paragraphs: [] as string[],
    quote: "",
  });

  useEffect(() => {
    if (data) {
      setForm({
        image: data.image || "",
        title: data.title || "Câu Chuyện Stackway",
        paragraphs: Array.isArray(data.paragraphs) ? data.paragraphs : [""],
        quote: data.quote || "",
      });
    }
  }, [data]);

  const addPara = () =>
    setForm((f) => ({ ...f, paragraphs: [...f.paragraphs, ""] }));
  const setPara = (i: number, v: string) =>
    setForm((f) => ({
      ...f,
      paragraphs: f.paragraphs.map((p, idx) => (idx === i ? v : p)),
    }));
  const removePara = (i: number) =>
    setForm((f) => ({
      ...f,
      paragraphs: f.paragraphs.filter((_, idx) => idx !== i),
    }));

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">Câu chuyện Stackway</h3>
      <div className="space-y-4">
        <ImageField
          label="Ảnh"
          value={form.image}
          onChange={(url) => {
            setForm((f) => {
              const updated = { ...f, image: url };
              onSave({ ...updated, paragraphs: updated.paragraphs.filter(Boolean) });
              return updated;
            });
          }}
          objectId={objectId}
          objectType="AboutPage"
          objectField="story"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tiêu đề
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Các đoạn văn
          </label>
          {(form.paragraphs.length ? form.paragraphs : [""]).map((p, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <div className="flex-1">
                <RichTextEditor
                  id={`story-p-${i}`}
                  value={p}
                  onChange={(v) => setPara(i, v)}
                  height={120}
                />
              </div>
              <button
                type="button"
                onClick={() => removePara(i)}
                className="text-red-600 hover:text-red-800 px-2 self-start"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addPara}
            className="text-teal-600 hover:text-teal-800 text-sm"
          >
            <i className="ri-add-line mr-1"></i> Thêm đoạn
          </button>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Câu trích dẫn
          </label>
          <RichTextEditor
            id="story-quote"
            value={form.quote}
            onChange={(v) => setForm((f) => ({ ...f, quote: v }))}
            height={100}
          />
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() =>
            onSave({
              ...form,
              paragraphs: form.paragraphs.filter(Boolean),
            })
          }
          disabled={saving}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? "Đang lưu..." : "Lưu"}
        </button>
        {message.text && (
          <span
            className={`text-sm font-medium ${
              message.type === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
};

// --- Mission & Vision ---
const MissionVisionSection = ({
  mission,
  vision,
  onSave,
  saving,
  message,
}: {
  mission?: { title: string; content: string };
  vision?: { title: string; content: string };
  onSave: (d: { mission: any; vision: any }) => void;
  saving: boolean;
  message: { type: string; text: string };
}) => {
  const [m, setM] = useState({
    title: "Sứ Mệnh",
    content: "",
  });
  const [v, setV] = useState({
    title: "Tầm Nhìn",
    content: "",
  });

  useEffect(() => {
    if (mission) setM({ title: mission.title || "Sứ Mệnh", content: mission.content || "" });
    if (vision) setV({ title: vision.title || "Tầm Nhìn", content: vision.content || "" });
  }, [mission, vision]);

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">
        Sứ mệnh & Tầm nhìn
      </h3>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-800">Sứ mệnh</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tiêu đề
            </label>
            <input
              type="text"
              value={m.title}
              onChange={(e) => setM((x) => ({ ...x, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nội dung
            </label>
            <RichTextEditor
              id="mission-content"
              value={m.content}
              onChange={(v) => setM((x) => ({ ...x, content: v }))}
              height={180}
            />
          </div>
        </div>
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-800">Tầm nhìn</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tiêu đề
            </label>
            <input
              type="text"
              value={v.title}
              onChange={(e) => setV((x) => ({ ...x, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nội dung
            </label>
            <RichTextEditor
              id="vision-content"
              value={v.content}
              onChange={(v) => setV((x) => ({ ...x, content: v }))}
              height={180}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => onSave({ mission: m, vision: v })}
          disabled={saving}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? "Đang lưu..." : "Lưu"}
        </button>
        {message.text && (
          <span
            className={`text-sm font-medium ${
              message.type === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
};

// --- Values Section ---
const ValuesSection = ({
  data,
  onSave,
  saving,
  message,
}: {
  data?: AboutPageData["values"];
  onSave: (d: NonNullable<AboutPageData["values"]>) => void;
  saving: boolean;
  message: { type: string; text: string };
}) => {
  const [form, setForm] = useState({
    heading: "Giá Trị Cốt Lõi",
    subheading:
      "Những giá trị định hướng mọi hành động và quyết định của chúng tôi",
    items: [] as Array<{
      icon: string;
      title: string;
      description: string;
      gradient: string;
    }>,
  });

  useEffect(() => {
    if (data) {
      setForm({
        heading: data.heading || "Giá Trị Cốt Lõi",
        subheading:
          data.subheading ||
          "Những giá trị định hướng mọi hành động và quyết định của chúng tôi",
        items: Array.isArray(data.items) ? data.items : [],
      });
    }
  }, [data]);

  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          icon: "ri-star-line",
          title: "",
          description: "",
          gradient: GRADIENT_OPTIONS[0].value,
        },
      ],
    }));
  const updateItem = (
    i: number,
    field: string,
    value: string
  ) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) =>
        idx === i ? { ...it, [field]: value } : it
      ),
    }));
  const removeItem = (i: number) =>
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i),
    }));

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">Giá trị cốt lõi</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tiêu đề chính
          </label>
          <input
            type="text"
            value={form.heading}
            onChange={(e) =>
              setForm((f) => ({ ...f, heading: e.target.value }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mô tả phụ
          </label>
          <input
            type="text"
            value={form.subheading}
            onChange={(e) =>
              setForm((f) => ({ ...f, subheading: e.target.value }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Các giá trị
            </label>
            <button
              type="button"
              onClick={addItem}
              className="text-teal-600 hover:text-teal-800 text-sm"
            >
              <i className="ri-add-line mr-1"></i> Thêm
            </button>
          </div>
          <div className="space-y-4">
            {form.items.map((it, i) => (
              <div
                key={i}
                className="p-4 border border-gray-200 rounded-lg space-y-3"
              >
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">#{i + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Icon (vd: ri-lightbulb-flash-line)"
                  value={it.icon}
                  onChange={(e) => updateItem(i, "icon", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  placeholder="Tiêu đề"
                  value={it.title}
                  onChange={(e) => updateItem(i, "title", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                <RichTextEditor
                  id={`values-desc-${i}`}
                  value={it.description}
                  onChange={(v) => updateItem(i, "description", v)}
                  height={100}
                />
                <select
                  value={it.gradient}
                  onChange={(e) =>
                    updateItem(i, "gradient", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {GRADIENT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => onSave(form)}
          disabled={saving}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? "Đang lưu..." : "Lưu"}
        </button>
        {message.text && (
          <span
            className={`text-sm font-medium ${
              message.type === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
};

// --- Brand Identity ---
const BrandIdentitySection = ({
  data,
  onSave,
  saving,
  message,
  objectId,
}: {
  data?: AboutPageData["brandIdentity"];
  onSave: (d: NonNullable<AboutPageData["brandIdentity"]>) => void;
  saving: boolean;
  message: { type: string; text: string };
  objectId: string;
}) => {
  const [form, setForm] = useState({
    badge: "NHẬN DIỆN THƯƠNG HIỆU STACKWAY",
    slogan: "",
    logoMeaning: [] as string[],
    image: "",
  });

  useEffect(() => {
    if (data) {
      setForm({
        badge: data.badge || "NHẬN DIỆN THƯƠNG HIỆU STACKWAY",
        slogan: data.slogan || "",
        logoMeaning: Array.isArray(data.logoMeaning) ? data.logoMeaning : [""],
        image: data.image || "",
      });
    }
  }, [data]);

  const setLogoPara = (i: number, v: string) =>
    setForm((f) => ({
      ...f,
      logoMeaning: f.logoMeaning.map((p, idx) => (idx === i ? v : p)),
    }));
  const addLogoPara = () =>
    setForm((f) => ({
      ...f,
      logoMeaning: [...f.logoMeaning, ""],
    }));
  const removeLogoPara = (i: number) =>
    setForm((f) => ({
      ...f,
      logoMeaning: f.logoMeaning.filter((_, idx) => idx !== i),
    }));

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">
        Nhận diện thương hiệu
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Badge
          </label>
          <input
            type="text"
            value={form.badge}
            onChange={(e) =>
              setForm((f) => ({ ...f, badge: e.target.value }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slogan
          </label>
          <RichTextEditor
            id="brand-slogan"
            value={form.slogan}
            onChange={(v) => setForm((f) => ({ ...f, slogan: v }))}
            height={80}
            placeholder="StackWay – Con đường xây dựng nền tảng số vững chắc"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ý nghĩa logo (các đoạn)
          </label>
          {(form.logoMeaning.length ? form.logoMeaning : [""]).map((p, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <div className="flex-1">
                <RichTextEditor
                  id={`brand-logo-${i}`}
                  value={p}
                  onChange={(v) => setLogoPara(i, v)}
                  height={120}
                />
              </div>
              <button
                type="button"
                onClick={() => removeLogoPara(i)}
                className="text-red-600 hover:text-red-800 px-2 self-start"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addLogoPara}
            className="text-teal-600 hover:text-teal-800 text-sm"
          >
            <i className="ri-add-line mr-1"></i> Thêm đoạn
          </button>
        </div>
        <ImageField
          label="Ảnh"
          value={form.image}
          onChange={(url) => {
            setForm((f) => {
              const updated = { ...f, image: url };
              onSave({ ...updated, logoMeaning: updated.logoMeaning.filter(Boolean) });
              return updated;
            });
          }}
          objectId={objectId}
          objectType="AboutPage"
          objectField="brandIdentity"
        />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() =>
            onSave({
              ...form,
              logoMeaning: form.logoMeaning.filter(Boolean),
            })
          }
          disabled={saving}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? "Đang lưu..." : "Lưu"}
        </button>
        {message.text && (
          <span
            className={`text-sm font-medium ${
              message.type === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
};

// --- Services Section ---
const ServicesSection = ({
  data,
  onSave,
  saving,
  message,
  objectId,
}: {
  data?: AboutPageData["services"];
  onSave: (d: NonNullable<AboutPageData["services"]>) => void;
  saving: boolean;
  message: { type: string; text: string };
  objectId: string;
}) => {
  const [form, setForm] = useState({
    heading: "Ba Lĩnh Vực Cốt Lõi",
    subheading:
      "Chúng tôi tập trung phát triển ba mảng hoạt động chính",
    items: [] as Array<{
      image: string;
      icon: string;
      title: string;
      description: string;
    }>,
  });

  useEffect(() => {
    if (data) {
      setForm({
        heading: data.heading || "Ba Lĩnh Vực Cốt Lõi",
        subheading:
          data.subheading ||
          "Chúng tôi tập trung phát triển ba mảng hoạt động chính",
        items: Array.isArray(data.items) ? data.items : [],
      });
    }
  }, [data]);

  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        { image: "", icon: "ri-building-line", title: "", description: "" },
      ],
    }));
  const updateItem = (
    i: number,
    field: string,
    value: string
  ) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) =>
        idx === i ? { ...it, [field]: value } : it
      ),
    }));
  const removeItem = (i: number) =>
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i),
    }));

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">
        Ba lĩnh vực cốt lõi
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tiêu đề chính
          </label>
          <input
            type="text"
            value={form.heading}
            onChange={(e) =>
              setForm((f) => ({ ...f, heading: e.target.value }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mô tả phụ
          </label>
          <input
            type="text"
            value={form.subheading}
            onChange={(e) =>
              setForm((f) => ({ ...f, subheading: e.target.value }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Các lĩnh vực
            </label>
            <button
              type="button"
              onClick={addItem}
              className="text-teal-600 hover:text-teal-800 text-sm"
            >
              <i className="ri-add-line mr-1"></i> Thêm
            </button>
          </div>
          <div className="space-y-6">
            {form.items.map((it, i) => (
              <div
                key={i}
                className="p-4 border border-gray-200 rounded-lg space-y-3"
              >
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">#{i + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
                <ImageField
                  label="Ảnh"
                  value={it.image}
                  onChange={(url) => {
                    setForm((f) => {
                      const updated = {
                        ...f,
                        items: f.items.map((item, idx) =>
                          idx === i ? { ...item, image: url } : item
                        ),
                      };
                      onSave(updated);
                      return updated;
                    });
                  }}
                  objectId={objectId}
                  objectType="AboutPage"
                  objectField={`services-${i}`}
                />
                <input
                  type="text"
                  placeholder="Icon (vd: ri-building-line)"
                  value={it.icon}
                  onChange={(e) => updateItem(i, "icon", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  placeholder="Tiêu đề"
                  value={it.title}
                  onChange={(e) => updateItem(i, "title", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                <RichTextEditor
                  id={`services-desc-${i}`}
                  value={it.description}
                  onChange={(v) => updateItem(i, "description", v)}
                  height={150}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => onSave(form)}
          disabled={saving}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? "Đang lưu..." : "Lưu"}
        </button>
        {message.text && (
          <span
            className={`text-sm font-medium ${
              message.type === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
};

export default AboutPageManagement;
